import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import heroIllustration from '@/assets/home/supply-platform.png';
import enterpriseCardImg from '@/assets/home/enterprise-cooperation-card.png';
import creatorCardImg from '@/assets/home/creator-recruitment-card.png';
import { fetchQueryPublicProviders } from '@/service/api/provider';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import { useGeoStore } from '@/stores/geoStore';
import CityCascader from '@/components/common/CityCascader';
import FilterTabs from '@/components/common/FilterTabs';
import ConfirmModal from '@/components/common/ConfirmModal';
import Pagination from '@/components/common/Pagination';
import { useAuth } from '@/hooks/useAuth';
import { useAssetRenewStore } from '@/stores';
import { formatCurrencyPrice } from '@/utils/currency';
import '@/styles/fluid-pages.css';

const ITEMS_PER_PAGE = 8;

export default function TalentsPage() {
  const navigate = useNavigate();
  const { isLoggedIn, user, isEnterprise } = useAuth();
  const isEnterpriseVerified = isEnterprise && user?.verified;
  const [showCertModal, setShowCertModal] = useState(false);

  const handleEnterpriseEntryClick = () => {
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    if (!isEnterpriseVerified) {
      setShowCertModal(true);
      return;
    }
    navigate('/provider-profile');
  };

  const handlePostDemandClick = () => {
    if (!isLoggedIn) {
      navigate('/auth');
      return;
    }
    if (!isEnterpriseVerified) {
      setShowCertModal(true);
      return;
    }
    navigate('/post-demand');
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [selectedCategoryCode, setSelectedCategoryCode] = useState('');
  const [selectedCityCode, setSelectedCityCode] = useState('');
  const [selectedProviderType, setSelectedProviderType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [talents, setTalents] = useState<Api.Provider.ProviderProfileCardResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isInitial, setIsInitial] = useState(true);

  const [cityOpen, setCityOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const cityRef = useRef<HTMLDivElement>(null);
  const typeRef = useRef<HTMLDivElement>(null);
  const lastFetchParamsRef = useRef<string | null>(null);

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();
  const { provinces, fetchProvinces, getCityNameById } = useGeoStore();

  // 初始化加载分类与省市数据
  useEffect(() => {
    fetchCategories().catch((err) => console.error('获取业务类目失败:', err));
    fetchProvinces().catch((err) => console.error('获取省市数据失败:', err));
  }, [fetchCategories, fetchProvinces]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) {
        setCityOpen(false);
      }
      if (typeRef.current && !typeRef.current.contains(e.target as Node)) {
        setTypeOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // 加载供给方人才列表
  const loadTalents = useCallback(async (force = false) => {
    const queryParams = {
      page: currentPage,
      page_size: ITEMS_PER_PAGE,
      category_code: selectedCategoryCode || undefined,
      city_code: selectedCityCode || undefined,
      provider_type: selectedProviderType || undefined,
      keyword: appliedKeyword.trim() || undefined,
    };
    const paramsKey = JSON.stringify(queryParams);

    if (!force && lastFetchParamsRef.current === paramsKey) {
      return;
    }
    lastFetchParamsRef.current = paramsKey;

    setLoading(true);
    try {
      const res = await fetchQueryPublicProviders(queryParams);
      setTalents(res?.items || []);
      setTotal(res?.total || 0);
    } catch (err) {
      console.error('加载人才列表失败:', err);
      setTalents([]);
      setTotal(0);
      lastFetchParamsRef.current = null;
    } finally {
      setLoading(false);
      setIsInitial(false);
    }
  }, [currentPage, selectedCategoryCode, selectedCityCode, selectedProviderType, appliedKeyword]);

  useEffect(() => {
    loadTalents();
  }, [loadTalents]);

  const assetCache = useAssetRenewStore((state) => state.assetCache);

  // 离场/路由切换时自动清空临时续期缓存
  useEffect(() => {
    return () => {
      useAssetRenewStore.getState().clearCache();
    };
  }, []);

  // 监听人才列表更新，自动收集服务商头像并在 Store 中全量注册上下文与预存凭证
  useEffect(() => {
    if (!talents.length) return;

    const renewRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    const singleAssetIds: string[] = [];

    talents.forEach((t) => {
      const avatar = t.avatar;
      if (!avatar) return;

      const avatarAssetId = avatar.id || (avatar as any).asset_id || (t as any).avatar_asset_id;
      if (!avatarAssetId) return;

      const assetIdStr = String(avatarAssetId);
      const accessUrl = avatar.access_url || (t as any).avatar_url;
      const accessExpires = avatar.access_expires;

      if (accessUrl) {
        useAssetRenewStore.getState().setAssetCacheItem(assetIdStr, accessUrl, accessExpires);
      }

      // 1. 优先直接获取后端下发的 access_resource 上下文
      const accessResource = (avatar as any).access_resource;
      if (accessResource && accessResource.resource_type && accessResource.resource_id) {
        renewRequests.push({
          resource_type: accessResource.resource_type,
          resource_id: String(accessResource.resource_id),
          asset_ids: accessResource.asset_ids?.length
            ? accessResource.asset_ids.map(String)
            : [assetIdStr],
        });
      } else {
        // 2. 兜底回退：头像使用 ACCOUNT 资源类型，resource_id 为用户账号 ID
        const accountId = String((t as any).user_id || (t as any).account_id || t.id || '');
        if (accountId) {
          renewRequests.push({
            resource_type: 'ACCOUNT',
            resource_id: accountId,
            asset_ids: [assetIdStr],
          });
        } else {
          singleAssetIds.push(assetIdStr);
        }
      }
    });

    if (renewRequests.length) {
      useAssetRenewStore.getState().renewBatchResources(renewRequests).catch(() => {});
    }

    singleAssetIds.forEach((assetId) => {
      useAssetRenewStore.getState().renewSingleAsset(assetId, 'ACCOUNT').catch(() => {});
    });
  }, [talents]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lastFetchParamsRef.current = null;
    setAppliedKeyword(searchQuery);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    lastFetchParamsRef.current = null;
    setSearchQuery('');
    setAppliedKeyword('');
    setSelectedCategoryCode('');
    setSelectedCityCode('');
    setSelectedProviderType('');
    setCurrentPage(1);
  };

  // 整理展开的所有城市列表（供下拉筛选）
  const allCities = provinces.flatMap((p) => p.children || []);

  const providerTypeOptions = [
    { label: '全部类型', value: '' },
    { label: '个人创作者', value: 'PERSONAL' },
    { label: '企业团队', value: 'ENTERPRISE' },
  ];

  return (
    <div className="talents-page min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <div className="pt-24 md:pt-28 pb-6 md:pb-8">
        <div className="site-shell max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
            {/* Left */}
            <div>
              <div className="relative inline-block">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight relative z-10">
                  人才广场
                </h1>
                <span className="absolute bottom-0.5 left-0 right-0 h-2.5 bg-[#a7f3d0]/80 -z-0 rounded-full"></span>
              </div>
              <p className="text-foreground-500 text-xs md:text-sm mt-1.5">
                汇聚AI创作人才，连接无限创意可能
              </p>
            </div>

            {/* Right: 2 Entrance Image Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 shrink-0 w-full md:w-auto">
              {/* Card 1 (左侧): 优质创作者招募计划 (成为创作者/立即加入) */}
              <div
                onClick={handleEnterpriseEntryClick}
                className="group relative w-full sm:w-[260px] md:w-[280px] lg:w-[320px] aspect-[2.22/1] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 shadow-2xs hover:shadow-md hover:scale-[1.015]"
              >
                <img
                  src={creatorCardImg}
                  alt="优质创作者招募计划"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Card 2 (右侧): 企业合作专场 (发布需求) */}
              <div
                onClick={handlePostDemandClick}
                className="group relative w-full sm:w-[260px] md:w-[280px] lg:w-[320px] aspect-[2.22/1] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 shadow-2xs hover:shadow-md hover:scale-[1.015]"
              >
                <img
                  src={enterpriseCardImg}
                  alt="企业合作专场"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters: Category & City & Provider Type & Search */}
      <div className="top-16 z-30 backdrop-blur-md">
        <div className="site-shell max-w-7xl mx-auto px-4 md:px-8 py-4 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Category Filter Tabs */}
            <FilterTabs
              label=""
              options={categories.map((cat) => ({
                label: cat.name,
                value: cat.code || cat.id || '',
              }))}
              value={selectedCategoryCode}
              onChange={(val) => {
                setSelectedCategoryCode(val);
                setCurrentPage(1);
              }}
              allLabel="全部人才"
              allValue=""
              activeItemClassName="bg-[#10b981] text-white font-medium shadow-2xs"
              inactiveItemClassName="bg-transparent text-gray-700 hover:text-[#10b981] font-normal"
              itemClassName="text-xs md:text-sm px-4 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer shrink-0 select-none"
            />

            {/* Dropdown Filters & Search */}
            <div className="flex items-center gap-3 shrink-0 flex-wrap sm:flex-nowrap">
              {/* Search Bar Input */}
              <form onSubmit={handleSearchSubmit} className="relative w-44 sm:w-56">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索姓名、专长..."
                  className="w-full h-9 pl-9 pr-3 rounded-full border border-background-300 bg-white text-xs text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                />
              </form>

              {/* City Dropdown */}
              <div className="w-[130px] sm:w-[150px]">
                <CityCascader
                  value={selectedCityCode}
                  onChange={(cityId) => {
                    setSelectedCityCode(cityId ? String(cityId) : '');
                    setCurrentPage(1);
                  }}
                  placeholder="全部城市"
                  icon={<i className="ri-map-pin-line text-primary-500"></i>}
                  selectClassName="h-9 px-3.5 rounded-full text-xs border-background-300 hover:border-primary-300"
                />
              </div>

              {/* Provider Type Dropdown */}
              <div ref={typeRef} className="relative">
                <button
                  onClick={() => setTypeOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-full border border-background-300 bg-white text-foreground-600 transition-all cursor-pointer whitespace-nowrap hover:border-primary-300 hover:text-primary-600 h-9"
                >
                  {providerTypeOptions.find((o) => o.value === selectedProviderType)?.label || '全部类型'}
                  <i className={`ri-arrow-down-s-line transition-transform ${typeOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {typeOpen && (
                  <div className="absolute right-0 top-full mt-2 w-36 bg-white rounded-xl border border-background-200 shadow-md py-1.5 z-40">
                    {providerTypeOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          if (selectedProviderType === opt.value) {
                            setTypeOpen(false);
                            return;
                          }
                          setSelectedProviderType(opt.value);
                          setTypeOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left text-xs px-4 py-2 transition-colors cursor-pointer ${selectedProviderType === opt.value
                          ? 'text-primary-600 font-medium bg-primary-50'
                          : 'text-foreground-600 hover:bg-gray-50'
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Talent Cards Grid */}
      <div className="site-shell max-w-7xl mx-auto px-4 md:px-8 pt-0 pb-12 relative min-h-[380px] w-full flex-1 flex flex-col">
        {isInitial && loading ? (
          /* 骨架屏 */
          <div className="talents-card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-background-200/80 p-6 sm:p-7 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-gray-200"></div>
                  <div className="flex-1 space-y-2">
                    <div className="w-24 h-5 bg-gray-200 rounded"></div>
                    <div className="w-16 h-4 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="flex gap-2 mb-3">
                  <div className="w-12 h-5 bg-gray-200 rounded-full"></div>
                  <div className="w-12 h-5 bg-gray-200 rounded-full"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded mb-5 w-full"></div>
                <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                  <div className="w-16 h-4 bg-gray-200 rounded"></div>
                  <div className="w-10 h-4 bg-gray-200 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : talents.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-background-200/60 my-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-user-search-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm">没有找到匹配的人才</p>
            {(selectedCategoryCode || selectedCityCode || selectedProviderType || appliedKeyword) && (
              <button
                onClick={resetFilters}
                className="mt-3 text-sm text-primary-600 hover:text-primary-500 cursor-pointer"
              >
                清除所有筛选条件
              </button>
            )}
          </div>
        ) : (
          <div className={`transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
            <div className="talents-card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {talents.map((talent) => (
                <TalentCard
                  key={talent.id}
                  talent={talent}
                  getCategoryNameByCode={getCategoryNameByCode}
                  getCityNameById={getCityNameById}
                />
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isInitial && talents.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            loading={loading}
            className="pt-8"
          />
        )}
      </div>

      <ConfirmModal
        open={showCertModal}
        type="warning"
        title="需要完成企业认证"
        content="人才入驻与供给方服务功能仅面向通过认证的企业账号开放。请先完成企业认证。"
        confirmText="前往企业认证"
        cancelText="取消"
        onConfirm={() => {
          setShowCertModal(false);
          navigate('/certification');
        }}
        onCancel={() => setShowCertModal(false)}
      />

    </div>
  );
}

/** 根据分类名称获取浅色彩胶囊标签样式 */
function getCategoryTagStyle(catName?: string) {
  if (!catName) return 'bg-emerald-50 text-emerald-600 border border-emerald-100/50';
  if (catName.includes('宣传') || catName.includes('企业')) {
    return 'bg-blue-50 text-blue-600 border border-blue-100/50';
  }
  if (catName.includes('海报') || catName.includes('设计') || catName.includes('策划')) {
    return 'bg-amber-50 text-amber-600 border border-amber-100/50';
  }
  if (catName.includes('漫剧') || catName.includes('动画') || catName.includes('插画')) {
    return 'bg-purple-50 text-purple-600 border border-purple-100/50';
  }
  return 'bg-emerald-50 text-emerald-600 border border-emerald-100/50';
}

function TalentCard({
  talent,
  getCategoryNameByCode,
  getCityNameById,
}: {
  talent: Api.Provider.ProviderProfileCardResponse;
  getCategoryNameByCode: (code?: string) => string;
  getCityNameById: (id?: string) => string;
}) {
  const assetCache = useAssetRenewStore((state) => state.assetCache);
  const avatarAssetId = talent.avatar?.id || (talent.avatar as any)?.asset_id || (talent as any).avatar_asset_id;
  const avatarUrl =
    (avatarAssetId && assetCache[String(avatarAssetId)]?.url) ||
    talent.avatar?.access_url ||
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80';
  const displayName = talent.display_name || talent.enterprise_name_masked || '创作者';
  const locationName = talent.city_name || getCityNameById(talent.city_code) || '全国 / 远程';

  // 展开分类或技能标签名称
  const rawCategories =
    talent.category_codes && talent.category_codes.length > 0
      ? talent.category_codes.map((code) => getCategoryNameByCode(code))
      : talent.skill_tags && talent.skill_tags.length > 0
        ? talent.skill_tags
        : [];
  const categoryList = rawCategories.filter(Boolean);

  // 格式化日薪显示
  const rateText =
    talent.daily_rate !== undefined && talent.daily_rate > 0
      ? `${formatCurrencyPrice(talent.daily_rate, talent.currency)}/天`
      : '面议';

  const providerTypeLabel =
    talent.provider_type === 'ENTERPRISE' ? '企业团队' : talent.provider_type === 'PERSONAL' ? '个人创作者' : '';

  return (
    <Link
      to={`/talents/${talent.id}`}
      className="group bg-white rounded-2xl border border-gray-100/90 shadow-2xs hover:shadow-md hover:border-emerald-200 transition-all cursor-pointer p-6 sm:p-7 flex flex-col justify-between"
    >
      <div className="flex flex-col h-full">
        {/* Header: 头像与名称区域 */}
        <div className="flex items-center gap-4 mb-3">
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover shrink-0 border border-gray-100 shadow-2xs"
            loading="lazy"
          />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="font-bold text-foreground-950 text-base sm:text-lg group-hover:text-emerald-600 transition-colors truncate">
                {displayName}
              </h3>
              <i
                className="ri-checkbox-circle-fill text-[#10b981] text-lg shrink-0"
                title="认证创作者"
              ></i>
            </div>
            {providerTypeLabel && (
              <span className="inline-block text-xs text-foreground-400 mt-0.5">
                {providerTypeLabel}
              </span>
            )}
          </div>
        </div>

        {/* 标签列表：位于头像下方独占整行宽度 */}
        {categoryList.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categoryList.slice(0, 2).map((cat, idx) => (
              <span
                key={idx}
                className={`text-xs px-3 py-0.5 rounded-full font-medium ${getCategoryTagStyle(
                  cat
                )}`}
              >
                {cat}
              </span>
            ))}
          </div>
        )}

        {/* 简介描述 */}
        <p className="text-xs sm:text-sm text-foreground-500/90 leading-relaxed line-clamp-1 overflow-hidden mb-4 flex-1">
          {talent.intro_summary || '暂无个人简介描述'}
        </p>

        {/* 底部信息：地区与价格 */}
        <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2 mt-auto">
          <div className="flex items-center gap-1.5 text-xs sm:text-sm text-foreground-500 min-w-0">
            <i className="ri-map-pin-line text-foreground-400 text-base shrink-0"></i>
            <span className="truncate">{locationName}</span>
          </div>

          <span className="font-extrabold text-base sm:text-lg text-[#10b981] shrink-0 whitespace-nowrap">
            {rateText}
          </span>
        </div>
      </div>
    </Link>
  );
}
